import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Error, ErrorText, LoaderSkeleton, Pill, Search, TableDataV2 } from '@utilities';
import {
  readClientCredits,
  readClientUserCredits,
  updateClientCredits,
  updateClientUserCredits,
} from '@api/client';
import { CREDIT_TYPES } from '@api/client/utilities/constants';
import { USER_CREDITS_ITEM_COLUMNS } from '@api/client/utilities/helpers';
import { updateUserCreditsRemaining } from '@redux/slices/user';

import { makeColumns } from './utilities/helpers';
import AllocatedCreditsCell from './components/AllocatedCredits';
import { NegDiffCell, PosDiffCell } from './components/AllocatedCredits/components/DiffCells';
import ClientCreditCards from './components/ClientCreditCards';

import styles from './_index.module.scss';

const CreditManagement = () => {
  const [clientCredits, setClientCredits] = useState(null);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [originalHoldForFutureProjects, setOriginalHoldForFutureProjects] = useState(null);

  const isEditable = useSelector((state) => state.user?.data?.permissions?.canUpdateUserCredits);
  const userId = useSelector((state) => state.user?.data?.id);
  const dispatch = useDispatch();

  const totalUserCredits = useMemo(
    () => data.reduce((sum, row) => sum + (row.allocatedCredits || 0), 0),
    [data]
  );

  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return data;

    return data?.filter((row) => {
      if (!row.display) return false;
      const tokens = row.display.toLowerCase().split(/\\s|_/);
      return tokens.some((token) => token.includes(query));
    });
  }, [searchQuery, data]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const [userCreditsResponse, clientCreditsResponse] = await Promise.all([
          readClientUserCredits({ signal: controller.signal }),
          readClientCredits({ signal: controller.signal }),
        ]);

        setData(userCreditsResponse.data);
        setClientCredits(clientCreditsResponse.data);
        setOriginalHoldForFutureProjects(
          clientCreditsResponse.data.find((c) => c.type === CREDIT_TYPES.HOLD_FOR_FUTURE)?.value
        );
        setIsLoading(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('Failed to load credit information. Please try again later.');
          setIsLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      controller.abort();
    };
  }, []);

  const normalizeCredits = (rowCreditData) => {
    const get = (label) => rowCreditData.find((c) => c.label === label)?.value ?? 0;

    return {
      availableCredits: get('Available Credits'),
      holdForFutureProjects: get('Hold for Future Projects'),
      inFlightCredits: get('In Flight Credits'),
    };
  };

  const normalizedCredits = useMemo(() => {
    return clientCredits ? normalizeCredits(clientCredits) : null;
  }, [clientCredits]);

  const unassignedCredits = useMemo(() => {
    if (!normalizedCredits) return 0;

    const { availableCredits, holdForFutureProjects } = normalizedCredits;
    return availableCredits - holdForFutureProjects - totalUserCredits;
  }, [normalizedCredits, totalUserCredits]);

  const updateData = useCallback((newData) => {
    setData(newData);
  }, []);
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      const [clientCreditsResult, userCreditsResult] = await Promise.all([
        updateClientCredits(normalizedCredits),
        updateClientUserCredits(data),
      ]);
      setClientCredits(clientCreditsResult.data);
      setData(userCreditsResult.data);

      const holdForFuture = clientCreditsResult.data.find(
        (c) => c.type === CREDIT_TYPES.HOLD_FOR_FUTURE
      )?.value;
      setOriginalHoldForFutureProjects(holdForFuture);
      setError(null);
      const userCreditPayload = userCreditsResult.data.find((item) => item.id === userId);
      if (userCreditPayload) {
        dispatch(
          updateUserCreditsRemaining({ userCreditsRemaining: userCreditPayload.allocatedCredits })
        );
      }
      // dispatch(userUpdateClientCredits(clientCreditsResult.data));
    } catch (error) {
      console.error('Error saving credits:', error);
      setSaveError('Failed to save credit information. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const creditCards = useMemo(() => {
    if (!clientCredits) return [];
    return [
      ...clientCredits,
      {
        label: 'Unassigned Credits',
        tooltip:
          "Credits that haven't been allocated to specific users, used for surveys currently in progress, or set aside for future use",
        type: CREDIT_TYPES.UNASSIGNED,
        value: unassignedCredits,
      },
    ];
  }, [clientCredits, unassignedCredits]);

  const columns = useMemo(
    () =>
      makeColumns(
        AllocatedCreditsCell,
        NegDiffCell,
        PosDiffCell,
        USER_CREDITS_ITEM_COLUMNS,
        isEditable,
        styles,
        totalUserCredits,
        unassignedCredits,
        updateData
      ),
    [isEditable, totalUserCredits, unassignedCredits, updateData]
  );

  const isSaveDisabled = useMemo(() => {
    const userCreditsChanged = data.some((row) => row.allocatedCredits !== row.startingCredits);
    let holdChanged = false;
    if (clientCredits && originalHoldForFutureProjects !== null) {
      const currentHold = clientCredits.find((c) => c.type === CREDIT_TYPES.HOLD_FOR_FUTURE)?.value;
      holdChanged = currentHold !== originalHoldForFutureProjects;
    }
    return !(userCreditsChanged || holdChanged);
  }, [data, clientCredits, originalHoldForFutureProjects]);

  if (error) return <Error status={error?.response?.status} />;

  if (isLoading) {
    return (
      <LoaderSkeleton height={115}>
        <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="100" />
      </LoaderSkeleton>
    );
  }

  return (
    <>
      {isEditable && <Pill isDisabled={true} value="Admin" />}
      <ClientCreditCards
        cards={creditCards}
        isEditable={isEditable}
        originalHoldForFutureProjects={originalHoldForFutureProjects}
        setClientCredits={setClientCredits}
        unassignedCredits={unassignedCredits}
      />
      <Search
        className={styles['credit-management-search']}
        isSearching={isLoading && searchQuery.trim().length > 0}
        onChange={(query) => setSearchQuery(query)}
        placeholder="Search"
        value={searchQuery}
      />
      <div className={styles['credit-management-table-container']}>
        {filteredData.length === 0 && searchQuery.trim().length > 0 ? (
          <div className={styles['search-options-empty-message']}>
            <h3>No results found.</h3>
            <p>Try refining your search.</p>
          </div>
        ) : (
          <TableDataV2 columns={columns} data={filteredData} isLoading={isLoading} />
        )}
        {isEditable && (
          <div className={styles['save-button-container']}>
            {saveError && <ErrorText status={saveError?.response?.status} text={saveError} />}
            <Button
              className={styles['user-credits-save-button']}
              isDisabled={isSaveDisabled}
              isLoading={isSaving}
              onClick={handleSave}
              text="Save"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CreditManagement;
